import useMutation from "../../../../lib/hooks/useMutation";
import {Box, Col, DSButton, DSIconButton, DSIconShield, Row} from "@cdx/ds";
import {
  ArrowOverlay,
  Document,
  DropDownForChild,
  RevealSpinner,
  SubmitButton,
  ToggleButtonWithLabel,
  TooltipForChild,
  XForm,
  useNeoBag,
} from "@cdx/common";
import useStateIfLoaded from "../../../../lib/hooks/useStateIfLoaded";
import {MultiUserSelectWithLabel} from "../../../../components/MultiUserSelect";
import {projectsUsers} from "../../../../lib/utils";
import dsStyles from "@cdx/ds/css/index.css";
import Avatar from "../../../../components/Avatar";
import {TextBadge} from "../../../../components/xui/Badge";
import {getLimits} from "../../../../features/billing/billing-utils";
import {useInstance} from "../../../../lib/mate/mate-utils";

const FormSubmitButton = (props) => {
  const formBag = useNeoBag();
  return <SubmitButton as={DSButton} formBag={formBag} {...props} />;
};

const GuardianOverlay = ({close, overlayProps, deck, root}) => {
  const [doUpdate, isLoading] = useMutation("decks", "updateGuardians");
  const limits = getLimits(root.account);
  const isEnabled = limits.withGuardians === "true" || deck.hasGuardians;

  const {state, setState} = useStateIfLoaded(() => ({
    active: deck.hasGuardians,
    userIds: deck.guardians.map((g) => g.userId),
  }));

  const handleSubmit = () => {
    return doUpdate({id: deck.id, userIds: state.active ? state.userIds : []}).then(close);
  };

  return (
    <ArrowOverlay {...overlayProps}>
      <RevealSpinner show={isLoading} withCover size={50} />
      <Col
        styleChild
        sp="16px"
        pa="24px"
        borderWidth={1}
        borderColor="default"
        rounded={4}
        maxWidth="480px"
      >
        <XForm
          values={state}
          onChange={setState}
          rules={(vals) => ({
            active: [],
            userIds: vals.active ? [[(val) => val.length > 0, "Pick at least one Guardian"]] : [],
          })}
          onSubmit={handleSubmit}
        >
          <Row sp="12px">
            <TooltipForChild
              tooltip={!isEnabled ? "This feature is only available on the Pro plan" : null}
              targetIsDisabled={true}
            >
              <XForm.Field
                as={ToggleButtonWithLabel}
                name="active"
                label={null}
                className={dsStyles.pt["4px"]}
                disabled={!isEnabled}
              />
            </TooltipForChild>
            <Col sp="24px">
              <Col sp="8px">
                <Box bold size={16}>
                  Appoint Guardians{" "}
                  <TextBadge size="sm" color="active" inline>
                    Pro
                  </TextBadge>
                </Box>
                <Document size="md">
                  <p>
                    Guardians are automatically added to all reviews in this deck. Only guardians
                    may set a card to done and archive or move a card out of this deck.
                  </p>
                </Document>
              </Col>
              {state.active && (
                <XForm.Field
                  as={MultiUserSelectWithLabel}
                  name="userIds"
                  label={null}
                  getUsers={() => projectsUsers(root.account, [deck.project])}
                />
              )}
              {isEnabled && (
                <Col align="start">
                  <FormSubmitButton>Save</FormSubmitButton>
                </Col>
              )}
            </Col>
          </Row>
        </XForm>
      </Col>
    </ArrowOverlay>
  );
};

export const GuardianTooltip = ({deckId}) => {
  const deck = useInstance("deck", deckId);
  if (deck?.hasGuardians) {
    return (
      <Col sp="8px" pa="12px" colorTheme="active600">
        <Box color="primary" size={14} bold>
          Guardians
        </Box>
        <Col sp="4px">
          {deck.guardians.map((g) => (
            <Row key={g.user.id} align="center" sp="4px">
              <Avatar user={g.user} size={16} />{" "}
              <Box size={12} color="secondary">
                {g.user.name}
              </Box>
            </Row>
          ))}
        </Col>
      </Col>
    );
  } else {
    return <Box pa="4px">Manage Guardians</Box>;
  }
};

const DeckGuardiansButton = ({deck, root, canModifyDeck}) => {
  if (!canModifyDeck && !deck.hasGuardians) return null;

  return (
    <DropDownForChild
      setChildProps={({isOpen}) => ({
        tooltip: isOpen ? null : <GuardianTooltip deckId={deck.id} />,
        hidden: isOpen,
      })}
      renderOverlay={({close, ...overlayProps}) => (
        <GuardianOverlay
          overlayProps={overlayProps}
          close={close}
          deck={deck}
          root={root}
          key={deck.id}
        />
      )}
      overlayProps={{placement: "bottom", distanceFromAnchor: 20}}
    >
      <TooltipForChild targetIsDisabled={!canModifyDeck}>
        <DSIconButton
          variant="tertiary"
          negatePadding
          active={deck.hasGuardians}
          icon={<DSIconShield />}
          disabled={!canModifyDeck}
        />
      </TooltipForChild>
    </DropDownForChild>
  );
};

export default DeckGuardiansButton;
